import {
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { TbPlus, TbTrash } from 'react-icons/tb';
import { formatPartNumber } from '../../functions/formatPartNumber';
import {
    PartNumberEditorRow,
    nextPartNumberRowKey,
} from '../../utils/inventoryPartNumbers';

interface InventoryPartNumbersFieldProps {
    value: Array<PartNumberEditorRow>;
    onChange: (rows: Array<PartNumberEditorRow>) => void;
    isDisabled?: boolean;
    /** Optional error under the table (e.g. duplicates / empty). */
    error?: string;
}

const TrashIcon = TbTrash as React.ElementType;
const PlusIcon = TbPlus as React.ElementType;

/** Brand blue (`brand.solid` / Apple Blue). */
const PARTBAY_LOGO_BLUE = '#0071E3';

const InventoryPartNumbersField = ({
    value,
    onChange,
    isDisabled = false,
    error,
}: InventoryPartNumbersFieldProps) => {
    const updateRow = (key: string, patch: Partial<PartNumberEditorRow>) => {
        onChange(value.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    };

    const setPrimary = (key: string) => {
        onChange(value.map((row) => ({ ...row, isPrimary: row.key === key })));
    };

    const blurNormalize = (key: string) => {
        const row = value.find((r) => r.key === key);
        if (!row) return;
        const stripped = formatPartNumber(row.value);
        if (stripped !== row.value) {
            updateRow(key, { value: stripped });
        }
    };

    const addRow = () => {
        onChange([
            ...value,
            { key: nextPartNumberRowKey(), value: '', isPrimary: false },
        ]);
    };

    const removeRow = (key: string) => {
        if (value.length <= 1) return;
        const removing = value.find((r) => r.key === key);
        const next = value.filter((r) => r.key !== key);
        if (removing?.isPrimary && next.length > 0) {
            next[0] = { ...next[0], isPrimary: true };
        }
        onChange(next);
    };

    return (
        <Box>
            <Box overflow="hidden">
                <Table
                    size="sm"
                    variant="simple"
                    sx={{
                        'th, td': {
                            borderBottom: '1px solid',
                            borderColor: 'gray.200',
                        },
                        'tr:last-of-type td': {
                            borderBottom: 'none',
                        },
                    }}
                >
                    <Thead>
                        <Tr bg="#F3F4F6">
                            <Th
                                w="48px"
                                px={3}
                                py={2.5}
                                fontSize="xs"
                                fontWeight="semibold"
                                letterSpacing="0.04em"
                                textTransform="uppercase"
                                color="gray.600"
                                borderBottom="none"
                            >
                                {/* Primary radio */}
                            </Th>
                            <Th
                                px={3}
                                py={2.5}
                                fontSize="xs"
                                fontWeight="semibold"
                                letterSpacing="0.04em"
                                textTransform="uppercase"
                                color="gray.700"
                                borderBottom="none"
                            >
                                Part Number
                            </Th>
                            <Th w="44px" px={1} py={2.5} borderBottom="none" />
                        </Tr>
                    </Thead>
                    <Tbody>
                        {value.map((row) => (
                            <Tr key={row.key} bg="white">
                                <Td px={3} py={3} textAlign="center" verticalAlign="middle">
                                    <Box
                                        as="button"
                                        type="button"
                                        role="radio"
                                        aria-checked={row.isPrimary}
                                        aria-label={`Set ${row.value || 'row'} as primary`}
                                        disabled={isDisabled}
                                        w="16px"
                                        h="16px"
                                        borderRadius="full"
                                        borderWidth="2px"
                                        borderColor={
                                            row.isPrimary ? PARTBAY_LOGO_BLUE : 'gray.300'
                                        }
                                        bg={row.isPrimary ? PARTBAY_LOGO_BLUE : 'white'}
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        cursor={isDisabled ? 'not-allowed' : 'pointer'}
                                        opacity={isDisabled ? 0.5 : 1}
                                        transition="border-color 0.15s ease, background 0.15s ease"
                                        _hover={
                                            isDisabled
                                                ? undefined
                                                : { borderColor: PARTBAY_LOGO_BLUE }
                                        }
                                        onClick={() => {
                                            if (!isDisabled) setPrimary(row.key);
                                        }}
                                    >
                                        {row.isPrimary ? (
                                            <Box
                                                w="6px"
                                                h="6px"
                                                borderRadius="full"
                                                bg="white"
                                            />
                                        ) : null}
                                    </Box>
                                </Td>
                                <Td px={3} py={2} verticalAlign="middle">
                                    <Input
                                        variant="unstyled"
                                        fontSize="sm"
                                        fontFamily="mono"
                                        color="gray.800"
                                        value={row.value}
                                        onChange={(e) =>
                                            updateRow(row.key, { value: e.target.value })
                                        }
                                        onBlur={() => blurNormalize(row.key)}
                                        isDisabled={isDisabled}
                                        placeholder="Enter part number"
                                        _placeholder={{ color: 'gray.400' }}
                                    />
                                </Td>
                                <Td px={1} py={2} verticalAlign="middle">
                                    <IconButton
                                        aria-label="Remove part number"
                                        icon={<TrashIcon />}
                                        size="xs"
                                        variant="ghost"
                                        color="gray.400"
                                        _hover={{ color: 'gray.600', bg: 'gray.100' }}
                                        isDisabled={isDisabled || value.length <= 1}
                                        onClick={() => removeRow(row.key)}
                                    />
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            <HStack
                mt={0}
                px={3}
                py={3}
                justify="space-between"
                align="center"
                borderTopWidth="1px"
                borderColor="gray.200"
            >
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    leftIcon={<PlusIcon />}
                    onClick={addRow}
                    isDisabled={isDisabled}
                    color="gray.600"
                    fontWeight="medium"
                    px={1}
                    _hover={{ bg: 'transparent', color: 'gray.800' }}
                >
                    Add part number
                </Button>
                {error ? (
                    <Text fontSize="xs" color="red.500">
                        {error}
                    </Text>
                ) : null}
            </HStack>
        </Box>
    );
};

export default InventoryPartNumbersField;
